// The shared control kit (Android's SatoriControls, docs/PARITY.md "Shared control kit"): the rule
// that fixes everything is that WHITESPACE separates groups, never rows. Every surface built on these
// pieces looks like one app — use them instead of styling a one-off.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon, type IconName } from '../../design/icons';
import { t } from '../../i18n/i18n';
import { navigate } from '../router';
import { HistoryArrows } from '../shell/HistoryArrows';
import { useShell } from '../shell/ShellContext';

/** A main-column destination (Hilos, Actividad, Guardados, Borradores, Búsqueda). */
export function DestinationFrame({ icon, title, subtitle, actions, children, toolbar }: { icon: IconName; title: string; subtitle?: string; actions?: ReactNode; toolbar?: ReactNode; children: ReactNode }) {
  const shell = useShell();
  return (
    <section className="conversation-pane destination" aria-label={title}>
      <div className="wallpaper" />
      <header className="conversation-header">
        {/* Phone width hides the arrows: the one way back is to the list, as in a conversation. */}
        <button type="button" className="icon-button compact-back" onClick={() => shell && navigate({ kind: 'home', team: shell.team.name })} title={t('web.nav.backToList')} aria-label={t('web.nav.backToList')}>
          <Icon name="chevronLeft" size={18} strokeWidth={2} />
        </button>
        <HistoryArrows />
        <span style={{ color: 'var(--text-muted)', display: 'grid' }}>
          <Icon name={icon} size={18} />
        </span>
        <div className="title-block">
          <h1>{title}</h1>
          {subtitle ? <div className="subtitle">{subtitle}</div> : null}
        </div>
        {actions ? <div className="actions">{actions}</div> : null}
      </header>
      <div className="destination-body">
        {toolbar ? <div className="destination-toolbar">{toolbar}</div> : null}
        {children}
      </div>
    </section>
  );
}

/** The right-hand side panel (thread, channel info). Escape closes it. */
export function SidePanelFrame({ icon, title, subtitle, onClose, children, width, actions }: { icon: IconName; title: string; subtitle?: string; onClose(): void; children: ReactNode; width: number; actions?: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !document.querySelector('.dialog') && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <aside className="conversation-pane side-panel" style={{ flex: `0 0 ${width}px` }} aria-label={title}>
      <div className="wash" />
      <header className="conversation-header">
        <span style={{ color: 'var(--text-muted)', display: 'grid' }}>
          <Icon name={icon} size={18} />
        </span>
        <div className="title-block">
          <h1>{title}</h1>
          {subtitle ? <div className="subtitle">{subtitle}</div> : null}
        </div>
        {actions}
        <button className="icon-button" onClick={onClose} title={t('common.close')} aria-label={t('common.close')}>
          <Icon name="x" size={17} />
        </button>
      </header>
      <div className="side-panel-body">{children}</div>
    </aside>
  );
}

/** A modal sheet with a scrim. Escape and the scrim close it; focus moves inside on open. */
export function Dialog({ title, onClose, children, footer, width = 520 }: { title: string; onClose(): void; children: ReactNode; footer?: ReactNode; width?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  // The latest onClose without re-running effects: callers pass inline arrows, and re-running the
  // focus effect on every render pulled focus out of whatever field the reader was typing in.
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const first = ref.current?.querySelector<HTMLElement>('input, textarea, button:not(.dialog-close)');
    first?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close.current();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && close.current()}>
      <div className="dialog" role="dialog" aria-modal="true" aria-label={title} style={{ width: `min(${width}px, calc(100vw - 32px))` }} ref={ref}>
        <header className="dialog-header">
          <h2>{title}</h2>
          <button type="button" className="icon-button dialog-close" onClick={() => close.current()} aria-label={t('common.close')}>
            <Icon name="x" size={17} />
          </button>
        </header>
        <div className="dialog-body">{children}</div>
        {footer ? <footer className="dialog-footer">{footer}</footer> : null}
      </div>
    </div>
  );
}

/** A group of rows on a raised surface; groups are separated by whitespace (never rows). */
export function Group({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="group">
      {title ? <div className="group-title">{title}</div> : null}
      <div className="group-rows">{children}</div>
    </div>
  );
}

export function ListRow({ leading, title, subtitle, trailing, onClick, active, danger, title2 }: { leading?: ReactNode; title: ReactNode; subtitle?: ReactNode; trailing?: ReactNode; onClick?(): void; active?: boolean; danger?: boolean; title2?: string }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag className={`list-row${active ? ' active' : ''}${danger ? ' danger' : ''}${onClick ? ' clickable' : ''}`} onClick={onClick} title={title2}>
      {leading ? <span className="list-row-leading">{leading}</span> : null}
      <span className="list-row-text">
        <span className="list-row-title">{title}</span>
        {subtitle ? <span className="list-row-subtitle">{subtitle}</span> : null}
      </span>
      {trailing ? <span className="list-row-trailing">{trailing}</span> : null}
    </Tag>
  );
}

export function SearchInput({ value, onChange, placeholder, autoFocus, onEnter }: { value: string; onChange(v: string): void; placeholder: string; autoFocus?: boolean; onEnter?(): void }) {
  return (
    <label className="search-field">
      <Icon name="search" size={15} />
      <input type="search" spellCheck={false} value={value} placeholder={placeholder} autoFocus={autoFocus} onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onEnter?.()} aria-label={placeholder} />
    </label>
  );
}

export function Segmented<T extends string>({ value, options, onChange }: { value: T; options: Array<{ value: T; label: string; count?: number }>; onChange(v: T): void }) {
  return (
    <div className="segmented" role="tablist">
      {options.map((o) => (
        <button type="button" key={o.value} role="tab" aria-selected={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
          {o.count ? <span className="segmented-count">{o.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** Never a blank screen, never a spinner instead of content: a sentence and maybe one action. */
export function EmptyState({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="empty-state compact">
      <h2>{title}</h2>
      {body ? <p>{body}</p> : null}
      {action ? <div style={{ marginTop: 16 }}>{action}</div> : null}
    </div>
  );
}

/** A quiet inline status for a refresh in flight or failed — never a dialog, never full screen. */
export function InlineStatus({ kind, text, onRetry }: { kind: 'loading' | 'error'; text: string; onRetry?(): void }) {
  return (
    <div className={`inline-status ${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {text}
      {onRetry ? (
        <button onClick={onRetry} className="link-button">
          {t('common.retry')}
        </button>
      ) : null}
    </div>
  );
}

export function Button({ children, onClick, kind = 'secondary', disabled, type = 'button', icon }: { children: ReactNode; onClick?(): void; kind?: 'primary' | 'secondary' | 'danger' | 'quiet'; disabled?: boolean; type?: 'button' | 'submit'; icon?: IconName }) {
  return (
    <button type={type} className={`btn btn-${kind}`} onClick={onClick} disabled={disabled}>
      {icon ? <Icon name={icon} size={15} /> : null}
      {children}
    </button>
  );
}

/** Two-tap destructive confirm in place (the app's own idiom, ChannelMembersSheet): auto-disarms after 4 s. */
export function ConfirmButton({ label, confirmLabel, onConfirm, icon }: { label: string; confirmLabel: string; onConfirm(): void; icon?: IconName }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const id = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(id);
  }, [armed]);
  return (
    <button
      type="button"
      className={`btn btn-danger${armed ? ' armed' : ''}`}
      onClick={() => {
        if (armed) {
          setArmed(false);
          onConfirm();
        } else setArmed(true);
      }}
    >
      {icon ? <Icon name={icon} size={15} /> : null}
      {armed ? confirmLabel : label}
    </button>
  );
}
